/**
 * Cart Context (S2)
 * Manages shopping cart state and operations
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Cart, CartSummary, AddToCartRequest } from '../types';
import * as shopApi from '../services/shopApi';
import { getSessionId } from '../utils/session';
import { useToastStore } from '../stores/toastStore';
import { getErrorMessage } from '../utils/errorHandler';
import analytics, { EVENTS } from '../utils/analytics';

interface CartContextValue {
  cart: Cart | null;
  cartSummary: CartSummary | null;
  isLoading: boolean;
  error: string | null;

  // Cart operations
  addToCart: (variantId: number, quantity: number) => Promise<void>;
  updateCartItem: (itemId: number, quantity: number) => Promise<void>;
  removeCartItem: (itemId: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  refreshCartSummary: () => Promise<void>;

  // Sidebar visibility
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;

  // Session
  sessionId: string;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [cartSummary, setCartSummary] = useState<CartSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sessionId] = useState(() => getSessionId());

  // Fetch full cart
  const refreshCart = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await shopApi.getCart(sessionId);
      setCart(response.data);
    } catch (err: any) {
      console.error('Failed to fetch cart:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Fetch lightweight cart summary
  const refreshCartSummary = useCallback(async () => {
    try {
      const response = await shopApi.getCartSummary(sessionId);
      setCartSummary(response.data);
    } catch (err: any) {
      console.error('Failed to fetch cart summary:', err);
      // Don't set error for summary failures - it's non-critical
    }
  }, [sessionId]);

  // Add item to cart
  const addToCart = useCallback(async (variantId: number, quantity: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const request: AddToCartRequest = {
        variant_id: variantId,
        quantity,
        session_id: sessionId
      };

      const response = await shopApi.addToCart(request, sessionId);
      const updatedCart = response.data;
      setCart(updatedCart);
      analytics.track(EVENTS.ADDED_TO_CART, { variant_id: variantId, quantity });

      // Update summary from cart response (avoids redundant API call)
      setCartSummary({ item_count: updatedCart.item_count, subtotal: updatedCart.subtotal });
    } catch (err: any) {
      console.error('Failed to add to cart:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      useToastStore.getState().error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Update cart item quantity with optimistic updates
  const updateCartItem = useCallback(async (itemId: number, quantity: number) => {
    if (!cart) return;

    // Optimistic update - update UI immediately
    const optimisticCart = {
      ...cart,
      items: cart.items.map(item => {
        if (item.id === itemId) {
          const newLineTotal = Number(item.variant_price) * quantity;
          return { ...item, quantity, line_total: newLineTotal };
        }
        return item;
      })
    };

    // Recalculate totals
    const newSubtotal = optimisticCart.items.reduce((sum, item) =>
      sum + Number(item.line_total), 0
    );
    optimisticCart.subtotal = newSubtotal;
    optimisticCart.total = newSubtotal; // + tax + shipping
    optimisticCart.item_count = optimisticCart.items.reduce((sum, item) =>
      sum + item.quantity, 0
    );

    setCart(optimisticCart as any);

    try {
      setError(null);

      // Sync with backend in background
      const response = await shopApi.updateCartItem(itemId, quantity, sessionId);
      const updatedCart = response.data;
      setCart(updatedCart);

      // Update summary from cart response (avoids redundant API call)
      setCartSummary({ item_count: updatedCart.item_count, subtotal: updatedCart.subtotal });
    } catch (err: any) {
      console.error('Failed to update cart item:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      useToastStore.getState().error(errorMessage);

      // Revert optimistic update on error
      await refreshCart();
      throw new Error(errorMessage);
    }
  }, [cart, sessionId, refreshCart]);

  // Remove item from cart with optimistic updates
  const removeCartItem = useCallback(async (itemId: number) => {
    if (!cart) return;

    // Optimistic update - remove from UI immediately
    const optimisticCart = {
      ...cart,
      items: cart.items.filter(item => item.id !== itemId)
    };

    // Recalculate totals
    const newSubtotal = optimisticCart.items.reduce((sum, item) =>
      sum + Number(item.line_total), 0
    );
    optimisticCart.subtotal = newSubtotal;
    optimisticCart.total = newSubtotal;
    optimisticCart.item_count = optimisticCart.items.reduce((sum, item) =>
      sum + item.quantity, 0
    );

    setCart(optimisticCart as any);

    try {
      setError(null);

      // Sync with backend in background
      const response = await shopApi.removeCartItem(itemId, sessionId);
      const updatedCart = response.data;
      setCart(updatedCart);

      // Update summary from cart response (avoids redundant API call)
      setCartSummary({ item_count: updatedCart.item_count, subtotal: updatedCart.subtotal });
    } catch (err: any) {
      console.error('Failed to remove cart item:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      useToastStore.getState().error(errorMessage);

      // Revert optimistic update on error
      await refreshCart();
      throw new Error(errorMessage);
    }
  }, [cart, sessionId, refreshCart]);

  // Clear entire cart
  const clearCart = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await shopApi.clearCart(sessionId);

      // Set cart to empty state
      setCart({
        id: 0,
        session_id: sessionId,
        customer_id: undefined,
        items: [],
        item_count: 0,
        subtotal: 0,
        tax: 0,
        shipping: 0,
        total: 0,
        has_unavailable_items: false,
        created_at: '',
        updated_at: '',
        expires_at: undefined
      });

      // Update summary directly (avoids redundant API call)
      setCartSummary({ item_count: 0, subtotal: 0 });
    } catch (err: any) {
      console.error('Failed to clear cart:', err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Sidebar visibility
  const openCart = useCallback(() => {
    setIsCartOpen(true);
    // Refresh cart when opening
    refreshCart();
  }, [refreshCart]);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Load cart summary on mount
  useEffect(() => {
    refreshCartSummary();
  }, [refreshCartSummary]);

  const value = useMemo<CartContextValue>(() => ({
    cart,
    cartSummary,
    isLoading,
    error,
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    refreshCart,
    refreshCartSummary,
    isCartOpen,
    openCart,
    closeCart,
    sessionId
  }), [
    cart, cartSummary, isLoading, error,
    addToCart, updateCartItem, removeCartItem, clearCart,
    refreshCart, refreshCartSummary,
    isCartOpen, openCart, closeCart, sessionId
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * Hook to access cart context
 * Must be used within CartProvider
 */
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
